import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { OrderLine, OrderListItem, OrderResponse } from '@models/products-and-orders/orders/order.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { createCustomStateSetOnRegexHandler, createCustomStateSetOnUrlHandler, createStateResetHandler, guidRegex } from '../../helpers';
import { getAll, getOne, StateDictionary } from '../../helpers/store-base';
import { OrdersService } from './orders.service';

type OrdersState = {
  orders: OrderListItem[];
  isLoading: boolean;
  selectedOrder: OrderResponse | null;
  isSelectedOrderLoading: boolean;

  orderLines: OrderLine[];
  selectedOrderLine: OrderLine | null;
  isOrderLinesLoading: boolean;
  isSelectedOrderLineLoading: boolean;
};

const initialState: OrdersState = {
  orders: [],
  isLoading: false,
  selectedOrder: null,
  isSelectedOrderLoading: false,

  orderLines: [],
  selectedOrderLine: null,
  isOrderLinesLoading: false,
  isSelectedOrderLineLoading: false,
};

const ordersDictionary: StateDictionary<OrderListItem> = {
  entityList: 'orders',
  listLoading: 'isLoading',
  selectedEntity: 'selectedOrder',
  selectedEntityLoading: 'isSelectedOrderLoading',
  entityId: 'id',
  createLoading: '',
  sortFn: (a: OrderListItem, b: OrderListItem) => b.orderedBy.date.getTime() - a.orderedBy.date.getTime(),
};

export const OrdersStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('orders-store'),

  withComputed(store => ({
    selectedOrderId: computed(() => store.selectedOrder()?.id || null),
    selectedOrderLineId: computed(() => store.selectedOrderLine()?.id || null),
  })),

  withMethods((store, ordersService = inject(OrdersService), router = inject(Router)) => {
    createStateResetHandler<OrdersState>('/orders')(store, initialState);
    createCustomStateSetOnUrlHandler<OrdersState>('/orders', { selectedOrder: null })(store);
    createCustomStateSetOnRegexHandler<OrdersState>(new RegExp(`^/orders/(${guidRegex})/lines$`))(store, {
      selectedOrderLine: null,
    });

    createCustomStateSetOnRegexHandler<OrdersState>(new RegExp(`^/orders/(${guidRegex})$`))(store, {
      orderLines: [],
    });

    const repositoryMethods = {
      getAll: getAll<OrderListItem>({
        store,
        service: ordersService,
        dictionary: ordersDictionary,
      }),

      getOne: getOne<OrderResponse>({
        store,
        service: ordersService,
        dictionary: ordersDictionary as unknown as StateDictionary<OrderResponse>,
      }),

      getOrderLines: rxMethod<string>(
        pipe(
          tap(() => {
            patchState(store, { isOrderLinesLoading: true });
          }),
          switchMap(orderId =>
            ordersService.getOrderLines(orderId).pipe(
              tap(lines => {
                patchState(store, { orderLines: lines, isOrderLinesLoading: false });
              }),
              catchError(() => {
                patchState(store, { orderLines: [], isOrderLinesLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getOrderLine: rxMethod<{ orderId: string; lineId: string }>(
        pipe(
          tap(() => {
            patchState(store, { isSelectedOrderLineLoading: true });
          }),
          switchMap(({ orderId, lineId }) =>
            ordersService.getOrderLine(orderId, lineId).pipe(
              tap(line => {
                patchState(store, { selectedOrderLine: line, isSelectedOrderLineLoading: false });
              }),
              catchError(() => {
                patchState(store, { selectedOrderLine: null, isSelectedOrderLineLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const navigationActions = {
      goToActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['orders', id]);

        if (!result) return false;

        if (!store.isSelectedOrderLoading()) repositoryMethods.getOne(id);

        return true;
      },
      goToOrderLineActions: async (orderId: string, lineId: string): Promise<boolean> => {
        const result = await router.navigate(['orders', orderId, 'lines', lineId]);

        if (!result) return false;

        if (!store.isSelectedOrderLineLoading()) repositoryMethods.getOrderLine({ orderId, lineId });

        return true;
      },
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
    };
  }),
);

export interface OrdersStoreType {
  readonly orders: Signal<OrderListItem[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedOrder: Signal<OrderResponse | null>;
  readonly isSelectedOrderLoading: Signal<boolean>;
  readonly orderLines: Signal<OrderLine[]>;
  readonly selectedOrderLine: Signal<OrderLine | null>;
  readonly isOrderLinesLoading: Signal<boolean>;
  readonly isSelectedOrderLineLoading: Signal<boolean>;

  readonly selectedOrderId: Signal<string | null>;
  readonly selectedOrderLineId: Signal<string | null>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly goToActions: (id: string) => Promise<boolean>;
  readonly goToOrderLineActions: (orderId: string, lineId: string) => Promise<boolean>;
  readonly getOrderLines: (orderId: string) => void;
  readonly getOrderLine: (params: { orderId: string; lineId: string }) => void;
}
